import { TooltipV2, BadgeV3 } from '@cimplico/components';
import { ExclamationCircleIcon, ChatBubbleLeftRightIcon, InboxIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import { ClientQueryStatusEnum } from '../../types/client-query';
import { InternalCommentStatusEnum } from '../../types/internal-comment';
import { Worksheet } from '../../types/worksheet';
import { WorksheetChecklistItemStatusEnum } from '../../types/worksheet-checklist-item';
import plural from '../../utilities/plural';

type WorksheetOutstandingItemsProps = {
    /** The worksheet with outstanding items */
    worksheet: Worksheet;
    /** Whether the screen is considered small and to collapse columns */
    smallScreen: boolean;
};

function WorksheetOutstandingItems({ worksheet, smallScreen }: WorksheetOutstandingItemsProps): JSX.Element {
    const outstandingComments = worksheet.internalComments.filter((ic) => ic.status !== InternalCommentStatusEnum.RESOLVED).length;
    const outstandingQueries = worksheet.clientQueries.filter((cq) => cq.status !== ClientQueryStatusEnum.RESOLVED).length;
    const outstandingChecklist = worksheet.checklist.filter((ci) => ci.status === WorksheetChecklistItemStatusEnum.UNCHECKED).length;

    const totalOutstanding = outstandingComments + outstandingQueries + outstandingChecklist;

    const commentMessage = `${outstandingComments} outstanding ${plural(outstandingComments, 'internal comment')}`;
    const queryMessage = `${outstandingQueries} outstanding ${plural(outstandingQueries, 'client query', 'client queries')}`;
    const checklistMessage = `${outstandingChecklist} outstanding ${plural(outstandingChecklist, 'checklist item')}`;

    // Collapse all outstanding items into a single message list to show in a tooltip
    if (smallScreen) {
        const messages: string[] = [];

        if (outstandingComments > 0) {
            messages.push(commentMessage);
        }
        if (outstandingQueries > 0) {
            messages.push(queryMessage);
        }
        if (outstandingChecklist > 0) {
            messages.push(checklistMessage);
        }

        return (
            <div>
                {totalOutstanding > 0 && (
                    <TooltipV2 position="top" message={messages}>
                        <BadgeV3
                            label={totalOutstanding.toString()}
                            color="yellow"
                            leftIcon={<ExclamationCircleIcon />}
                            size="small"
                            rounded
                        />
                    </TooltipV2>
                )}
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center gap-1">
                {outstandingComments > 0 && (
                    <div>
                        <TooltipV2 position="top" message={commentMessage}>
                            <Link to={`${worksheet.id}?addon=internalComments`}>
                                <div className="flex items-center">
                                    <p className="text-xs text-gray-600 font-medium">{outstandingComments}</p>
                                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-gray-500 dark:text-white/[0.6] shrink-0" />
                                </div>
                            </Link>
                        </TooltipV2>
                    </div>
                )}
                {outstandingQueries > 0 && (
                    <div>
                        <TooltipV2 message={queryMessage} position="top">
                            <Link to={`${worksheet.id}?addon=clientQueries`}>
                                <div className="flex items-center">
                                    <p className="text-xs text-gray-600 font-medium">{outstandingQueries}</p>
                                    <InboxIcon className="w-5 h-5 text-gray-500 dark:text-white/[0.6] shrink-0" />
                                </div>
                            </Link>
                        </TooltipV2>
                    </div>
                )}

                {outstandingChecklist > 0 && (
                    <div>
                        <TooltipV2 message={checklistMessage} position="top">
                            <Link to={`${worksheet.id}?tab=checklist`}>
                                <div className="flex items-center">
                                    <p className="text-xs text-gray-600 font-medium">{outstandingChecklist}</p>
                                    <ClipboardDocumentCheckIcon className="w-5 h-5 text-gray-500 dark:text-white/[0.6] shrink-0" />
                                </div>
                            </Link>
                        </TooltipV2>
                    </div>
                )}
            </div>
        </div>
    );
}

export default WorksheetOutstandingItems;
